import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight, Zap, Cpu, Sun, Flame, Scissors, MapPin, Mail, Phone, Send } from "lucide-react";
import { RevealText, RevealLines, Reveal } from "@/lib/motion";
import { divisions, photos, TID, api, errMsg} from "@/lib/config";
import { useDivisionsVisibility } from "@/lib/divisionsVisibility";
import { useMedia } from "@/lib/media";

/* ─────────────────────────  HERO  ───────────────────────── */
export const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.12]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.15]);
  const { getSlot } = useMedia();
  const heroImg = getSlot("home-hero", photos.sede[0]);

  return (
    <section ref={ref} className="relative h-[100vh] min-h-[680px] w-full overflow-hidden pt-[72px]">
      {/* Background image with parallax */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={heroImg} alt="Sede Gefion Evolution al tramonto"
          className="w-full h-full object-cover" />
        {/* darken + orange spot */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/60 to-[#050505]" />
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_20%_35%,rgba(255,75,0,0.18),transparent_60%)]" />
      </motion.div>

      {/* Blueprint grid overlay */}
      <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />

      {/* Content */}
      <motion.div style={{ opacity }} className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <Reveal delay={0.1}>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-8 h-px bg-[#ff4b00]" />
                <span className="font-mono-tech text-white/70">◐ Gefion Evolution · Est.</span>
              </div>
            </Reveal>

            <RevealLines
              as="h1"
              className="font-display text-white hero-title text-[11vw] md:text-[6.4vw] leading-[0.9] max-w-full md:max-w-[92%]"
              lines={["IL POSTO", "DOVE LE IDEE", <span key="ideas">DIVENTANO <span className="text-[#ff4b00]">PROGETTI.</span></span>]}
              delay={0.35}
            />
          </div>

          <div className="md:col-span-4 md:pb-3">
            <Reveal delay={1.3}>
              <p className="text-white/70 text-sm md:text-[15px] leading-relaxed max-w-sm mb-8">
                Impianti elettrici, automazioni, fotovoltaico, antincendio e taglio laser
                su misura. Ingegneria concreta, dalla progettazione alla messa in opera.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/taglio-laser" data-testid={TID.heroCtaLaser} className="btn-primary">
                  <Scissors size={14} /> Taglio Laser Online
                </Link>
                <a href="#divisioni" data-testid={TID.heroCtaDivisions} className="btn-ghost">
                  Scopri le divisioni <ArrowRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Ticker at hero bottom */}
        <Reveal delay={1.6}>
          <div className="mt-16 border-t border-white/10 pt-6 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              ["◇", "15+ anni", "di esperienza sul campo"],
              ["◈", "300+", "impianti consegnati"],
              ["◉", "24/7", "assistenza & manutenzione"],
              ["◆", "Chiavi", "in mano"],
            ].map(([sym, big, small]) => (
              <div key={big} className="flex items-baseline gap-3">
                <span className="text-[#ff4b00] text-lg">{sym}</span>
                <div>
                  <div className="font-display text-2xl md:text-3xl leading-none">{big}</div>
                  <div className="font-mono-tech text-white/50 mt-2">{small}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </motion.div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 font-mono-tech text-[10px] text-white/40 flex items-center gap-2">
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>↓</motion.span>
        Scroll
      </div>
    </section>
  );
};

/* ─────────────────────────  MARQUEE  ───────────────────────── */
export const MarqueeStrip = () => {
  const items = ["◇ ELETTRICI", "◇ AUTOMAZIONI", "◇ FOTOVOLTAICO", "◇ ANTINCENDIO", "◇ TAGLIO LASER", "◇ DEPURAZIONE", "◇ POTABILIZZAZIONE"];
  return (
    <section className="py-16 md:py-24 border-y border-[#1a1a1a] bg-[#080808] overflow-hidden">
      <Marquee gradient={false} speed={40} pauseOnHover>
        {items.map((t, i) => (
          <span key={i} className="font-display stroke-title text-[14vw] md:text-[10vw] leading-none mx-8">
            {t}
          </span>
        ))}
      </Marquee>
    </section>
  );
};

/* ─────────────────────  MANIFESTO CHAPTERS  ────────────────────
   4 numbered chapters, alternating layout, with clipped frames. */

const iconOf = (id) => ({ elettrico: Zap, automazioni: Cpu, fotovoltaico: Sun, antiincendio: Flame }[id] || Zap);

const Chapter = ({ d, index }) => {
  const reversed = index % 2 === 1;
  const Icon = iconOf(d.id);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const { getSlot } = useMedia();
  const chapterImg = getSlot(`chapter-${d.id}`, d.hero);

  return (
    <div ref={ref} className="relative py-24 md:py-32 border-b border-[#141414]">
      <div className={`max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center ${reversed ? "" : ""}`}>
        {/* Photo */}
        <div className={`md:col-span-6 ${reversed ? "md:order-2" : ""}`}>
          <Reveal>
            <div className="relative">
              {/* Massive faded number behind photo */}
              <div className={`absolute -top-10 ${reversed ? "-right-4 md:-right-10" : "-left-4 md:-left-10"} font-display text-[22vw] md:text-[16vw] leading-none text-[#141414] pointer-events-none select-none`}>
                {d.number}
              </div>
              <motion.div style={{ y }} className="relative photo-frame aspect-[4/5] photo-spotlight">
                <img src={chapterImg} alt={d.title} />
              </motion.div>
            </div>
          </Reveal>
        </div>

        {/* Text */}
        <div className={`md:col-span-6 min-w-0 ${reversed ? "md:order-1 md:pr-10" : "md:pl-8"}`}>
          <Reveal delay={0.05}>
            <div className="flex items-center gap-3 mb-6">
              <span className="num-chip"><span className="dot" /> Capitolo {d.number}</span>
              <span className="font-mono-tech text-white/40">{d.tag}</span>
            </div>
          </Reveal>
          <RevealText text={d.title} className="font-display text-[8vw] md:text-[3.6vw] leading-[0.94] text-white" />
          <Reveal delay={0.15}>
            <div className="font-editorial text-white/60 text-3xl md:text-4xl mt-2 mb-8">— {d.subtitle}</div>
            <p className="text-white/70 text-base md:text-[17px] max-w-xl leading-relaxed mb-8">{d.lead}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-10">
              {d.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/80 text-sm">
                  <Icon size={14} className="text-[#ff4b00] mt-1 shrink-0" /> <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link to={`/divisione/${d.id}`} data-testid={`${TID.divisionCard}-${d.id}`}
              className="btn-ghost">
              Approfondisci <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export const Divisions = () => {
  const { visibility } = useDivisionsVisibility();
  const visible = divisions.filter((d) => visibility[d.id] !== false);
  return (
  <section id="divisioni" className="relative">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24 pb-8">
      <Reveal>
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#ff4b00]" />
          <span className="font-mono-tech text-white/70">Le nostre divisioni</span>
        </div>
      </Reveal>
      <RevealText text="Quattro rami. Un'unica ingegneria." className="font-display text-white text-[8vw] md:text-[4.4vw] leading-[0.95] max-w-5xl" />
      <Reveal delay={0.2}>
        <p className="text-white/60 max-w-xl mt-6 md:text-lg">
          Da Gefion Evolution ogni impianto è pensato, disegnato e assemblato dallo stesso team.
          Nessun subappalto invisibile — solo mani, teste e strumenti Made in Italy.
        </p>
      </Reveal>
    </div>
    {visible.map((d, i) => (
      <Chapter key={d.id} d={d} index={i} />
    ))}
  </section>
);
};

/* ─────────────────────  LASER CTA (Feature Row)  ────────────────── */
export const LaserCTA = () => (
  <section className="relative py-28 md:py-40 overflow-hidden border-b border-[#141414]">
    <div className="absolute inset-0">
      <img src={photos.laser[0]} alt="" className="w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_80%_40%,rgba(255,75,0,0.18),transparent_60%)]" />
    </div>
    <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
      <div className="md:col-span-8">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="num-chip"><span className="dot" /> Nuovo servizio · 05</span>
            <span className="font-mono-tech text-white/50">Preventivo online · 24h</span>
          </div>
        </Reveal>
        <RevealText text="Taglio laser" className="font-display text-white text-[13vw] md:text-[7.5vw] leading-[0.92]" />
        <RevealText text="on-demand." className="font-display text-[#ff4b00] text-[13vw] md:text-[7.5vw] leading-[0.92]" delay={0.3} />
        <Reveal delay={0.4}>
          <p className="text-white/70 max-w-xl mt-8 md:text-lg">
            Carica il tuo <span className="font-mono-tech text-white">DWG / DXF</span>, scegli il materiale
            (Acciaio fino a 10&nbsp;mm, Ferro fino a 15&nbsp;mm), ricevi un preventivo con
            spedizione calcolata. Un tecnico Gefion revisiona ogni disegno prima del pagamento.
          </p>
        </Reveal>
      </div>
      <div className="md:col-span-4">
        <Reveal delay={0.5}>
          <Link to="/taglio-laser" className="btn-primary w-full justify-center py-5 text-[13px]">
            <Scissors size={16} /> Avvia il preventivo
          </Link>
          <div className="mt-4 font-mono-tech text-[10px] text-white/40 text-center">
            ⌁ Automatico · Revisionato · Made-to-order
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ─────────────────────  ASSISTENZA TEASER  ────────────────── */
export const AssistenzaTeaser = () => (
  <section className="relative py-28 md:py-36 border-b border-[#141414] blueprint-grid">
    <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_20%_50%,rgba(0,85,255,0.10),transparent_60%)]" />
    <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="num-chip"><span className="dot" /> Capitolo 07 · Assistenza & SLA</span>
            </div>
          </Reveal>
          <RevealText text="Non ti lasciamo mai da solo." className="font-display text-white text-[8vw] md:text-[4.6vw] leading-[0.94]" />
          <Reveal delay={0.2}>
            <p className="text-white/70 max-w-xl mt-6 md:text-lg">
              Contratti di assistenza <span className="text-white">Standard, Extended e H24/7</span> per ogni divisione.
              Manutenzione programmata, interventi a tempo e ticket system per i nostri clienti.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-5 md:pl-10 md:border-l md:border-[#1a1a1a]">
          <div className="grid grid-cols-3 gap-2 mb-8">
            {[
              { code: "TIPO 1", label: "Standard", hours: "L-V · 8:30-17" },
              { code: "TIPO 2", label: "Extended", hours: "L-V · 7-19" },
              { code: "TIPO 3", label: "H24/7", hours: "24/7" },
            ].map((p, i) => (
              <div key={p.code} className={`border ${i === 1 ? "border-[#ff4b00]" : "border-[#1a1a1a]"} p-4`}>
                <div className={`font-mono-tech text-[10px] ${i === 1 ? "text-[#ff4b00]" : "text-white/40"}`}>{p.code}</div>
                <div className="font-display text-lg mt-2">{p.label}</div>
                <div className="font-mono-tech text-[9px] text-white/40 mt-1">{p.hours}</div>
              </div>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="flex flex-wrap gap-3">
              <Link to="/assistenza" data-testid="home-assist-cta" className="btn-primary">Scopri i piani</Link>
              <Link to="/ticket" data-testid="home-ticket-cta" className="btn-ghost">Apri un ticket</Link>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────  CONTACT  ────────────────── */
export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Richiesta informazioni", message: "" });
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) { toast.error("Accetta la privacy policy per procedere"); return; }
    if (form.message.length < 5) { toast.error("Scrivi un messaggio più lungo"); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      await api.post("/privacy/consent-log", { kind: "form", form: "contact", email: form.email });
      toast.success("Richiesta inviata! Ti risponderemo entro 24h.");
      setForm({ name: "", email: "", phone: "", subject: "Richiesta informazioni", message: "" });
      setPrivacy(false);
    } catch (err) {
      toast.error(errMsg(err, "Errore nell'invio"));
    } finally { setSending(false); }
  };

  return (
    <section id="contatti" className="py-24 md:py-32 relative">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#ff4b00]" />
              <span className="font-mono-tech text-white/70">Contatti · 06</span>
            </div>
          </Reveal>
          <RevealText text="Parliamo del tuo progetto." className="font-display text-white text-[8vw] md:text-[4vw] leading-[0.96]" />
          <Reveal delay={0.15}>
            <p className="text-white/60 mt-6 max-w-md">
              Scrivici. Rispondiamo entro 24 ore lavorative con un tecnico assegnato al tuo caso.
            </p>
            <div className="mt-10 space-y-4 font-mono-tech text-white/70">
              <div className="flex items-center gap-3"><Mail size={14} className="text-[#ff4b00]" /> <a className="hover:text-[#ff4b00]" href="mailto:info@gefion-evolution.it">info@gefion-evolution.it</a></div>
              <a href="https://maps.google.com/?q=Via+1%C2%B0+Maggio+6+Budoia+PN" target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-[#ff4b00]"><MapPin size={14} className="text-[#ff4b00] mt-1" /> <span>Via 1° Maggio, 6<br/>33070 Budoia (PN)<br/><span className="text-[10px] text-[#ff4b00]">→ apri in mappe</span></span></a>
            </div>
            <div className="mt-10 photo-frame aspect-[4/3] max-w-md">
              <img src={photos.sede[1]} alt="Sede Gefion" />
            </div>
            <div className="mt-6 relative max-w-md overflow-hidden border border-[#1a1a1a]" data-testid="contact-map">
              <div className="relative">
                <iframe
                  title="Sede Gefion Evolution — Via 1° Maggio 6, Budoia PN"
                  src="https://www.google.com/maps?q=Gefion+Evolution+Srl,+Via+1%C2%B0+Maggio+6,+33070+Budoia+PN,+Italia&z=17&hl=it&output=embed"
                  width="100%"
                  height="280"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) brightness(0.9)" }}
                  allowFullScreen
                />
                {/* Persistent business badge — always visible over the map */}
                <div className="absolute top-3 left-3 bg-[#0a0a0a]/95 backdrop-blur-sm border border-[#ff4b00]/40 px-3 py-2 pointer-events-none shadow-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#ff4b00] rounded-full" />
                    <div>
                      <div className="font-display text-white text-[11px] leading-none">GEFION S.R.L.</div>
                      <div className="font-mono-tech text-white/60 text-[9px] mt-1 leading-none">Via 1° Maggio 6 · Budoia (PN)</div>
                    </div>
                  </div>
                </div>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Gefion+Evolution+Srl+Via+1%C2%B0+Maggio+6+Budoia+PN"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-map-directions"
                className="block text-center font-mono-tech text-[11px] text-white/70 hover:text-[#ff4b00] py-3 border-t border-[#1a1a1a] bg-[#0a0a0a] transition-colors"
              >
                → Ottieni indicazioni stradali
              </a>
            </div>
          </Reveal>
        </div>

        <form className="md:col-span-7 md:pl-10 md:border-l md:border-[#1a1a1a]" onSubmit={submit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <label className="field-label">Nome e cognome</label>
              <input required data-testid={TID.contactName} className="field" value={form.name} onChange={on("name")} />
            </div>
            <div>
              <label className="field-label">Email</label>
              <input required type="email" data-testid={TID.contactEmail} className="field" value={form.email} onChange={on("email")} />
            </div>
            <div>
              <label className="field-label">Telefono</label>
              <input data-testid={TID.contactPhone} className="field" value={form.phone} onChange={on("phone")} />
            </div>
            <div>
              <label className="field-label">Oggetto</label>
              <select data-testid={TID.contactSubject} className="field" value={form.subject} onChange={on("subject")}>
                <option>Richiesta informazioni</option>
                <option>Impianti elettrici</option>
                <option>Automazioni</option>
                <option>Fotovoltaico</option>
                <option>Antincendio</option>
                <option>Taglio laser</option>
                <option>Assistenza / Manutenzione</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="field-label">Messaggio</label>
              <textarea required data-testid={TID.contactMessage} rows={5} className="field resize-none" value={form.message} onChange={on("message")} />
            </div>
          </div>
          <div className="mt-10 flex flex-col gap-5">
            <label className="flex items-start gap-3 text-white/70 text-sm cursor-pointer max-w-xl">
              <input type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)}
                data-testid="contact-privacy" className="mt-1 accent-[#ff4b00]" />
              <span>Ho letto e accetto la <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy Policy</Link>. Autorizzo il trattamento dei miei dati per rispondere alla richiesta. *</span>
            </label>
            <div className="flex items-center justify-between gap-6">
              <div className="font-mono-tech text-[10px] text-white/40 max-w-xs">
                I dati sono trattati esclusivamente per rispondere alla tua richiesta.
              </div>
              <button disabled={sending} data-testid={TID.contactSubmit} className="btn-primary">
                {sending ? "Invio..." : <>Invia richiesta <Send size={14} /></>}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

/* ─────────────────────  MANIFESTO / PHILOSOPHY  ────────────────── */
export const Philosophy = () => (
  <section className="py-28 md:py-40 border-b border-[#141414]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#ff4b00]" />
            <span className="font-mono-tech text-white/70">Manifesto</span>
          </div>
          <div className="num-chip"><span className="dot" /> 00 · Sul nostro modo di lavorare</div>
        </Reveal>
      </div>
      <div className="md:col-span-8">
        <RevealLines
          as="p"
          className="font-editorial text-white text-4xl md:text-6xl leading-[1.05]"
          lines={[
            "Costruire un impianto è",
            <>un atto di <em className="text-[#ff4b00] not-italic font-display">precisione</em>.</>,
            "Progettarlo, ancora di più.",
          ]}
        />
        <Reveal delay={0.3}>
          <p className="text-white/60 mt-10 max-w-2xl md:text-lg">
            Da quindici anni ci occupiamo di ingegneria elettrica, automazione, energia rinnovabile
            e trattamento acque. Nel 2026 abbiamo aperto una nuova officina di taglio laser e,
            da settembre, apriremo anche nuove divisioni impianti elettrici civili e industriali,
            Fotovoltaico e Antincendio — dall'idea al pezzo finito, senza intermediari.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
